import type { ResolvedPassport } from './concurrent';
import { SortType } from '@/types/api';

// 값이 없는 구성원은 해당 정렬 축의 마지막에 둔다. 0개월은 유효한 값이다.
function compareValue<T extends string | number>(
  a: T | null | undefined,
  b: T | null | undefined,
  descending = false,
): number {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  const result = a < b ? -1 : a > b ? 1 : 0;
  return descending ? -result : result;
}

/**
 * 조직 관점으로 변환한 뒤 DefaultOrganization과 같은 방향으로 정렬한다.
 * 직급 코드는 숫자로 변환하지 않는다(F1 등 문자 코드도 내려온다).
 * 원본 API 배열을 변경하지 않으며, 동률은 passportId로 순서를 고정한다.
 */
export function sortOrganizationPassports(
  passports: readonly ResolvedPassport[],
  sortType?: SortType,
  isDesc = false,
): ResolvedPassport[] {
  return [...passports].sort((a, b) => {
    const x = a.detail;
    const y = b.detail;
    // 리스트뷰 헤더의 방향은 기존 sortFilter와 동일하게 유지한다.
    const positionSort = sortType === SortType.positionCode;
    const rankDirection = positionSort && !isDesc ? -1 : 1;
    const primary =
      sortType === SortType.divisionName
        ? compareValue(x.divisionName, y.divisionName, isDesc) ||
          compareValue(x.departmentName, y.departmentName, isDesc)
        : sortType === SortType.name
        ? compareValue(x.name, y.name, isDesc)
        : sortType === SortType.email
        ? compareValue(x.email, y.email, isDesc)
        : 0;
    return (
      primary ||
      (Number(!!y.isLeader) - Number(!!x.isLeader)) * rankDirection ||
      (Number(!!y.isActingTeamLeader) - Number(!!x.isActingTeamLeader)) *
        rankDirection ||
      (Number(!!y.isPartLeader) - Number(!!x.isPartLeader)) * rankDirection ||
      compareValue(x.positionCode, y.positionCode, positionSort && !isDesc) ||
      compareValue(
        x.monthsInPosition,
        y.monthsInPosition,
        !positionSort || isDesc,
      ) ||
      compareValue(
        x.overallCareerMonths,
        y.overallCareerMonths,
        !positionSort || isDesc,
      ) ||
      compareValue(x.divisionName, y.divisionName) ||
      compareValue(x.departmentName, y.departmentName) ||
      compareValue(x.name, y.name) ||
      compareValue(x.email, y.email) ||
      compareValue(a.passportId, b.passportId) ||
      compareValue(x.organizationCode, y.organizationCode)
    );
  });
}
